import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import { toast } from 'react-hot-toast';
import { GET_PUBLIC_CAMPAIGN } from '../../graphql/queries';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { useTranslation } from 'react-i18next';
import ChannelIcon from '../campaigns/ChannelIcon';
import { Spinner } from '../common/Spinner';
import { storeRedirectIntent } from '../../utils/authRedirect';
import GlobalNavBar from '../layout/GlobalNavBar';
import AddressMapView from '../common/AddressMapView';

/* ─── Types ─── */
interface CampaignImage {
  fileKey?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
}

interface RecruitmentPeriod {
  startDate?: string;
  endDate?: string;
  alwaysRecruiting?: boolean;
}

interface PublishingCondition {
  sequence?: number;
  publishingChannel?: string;
  contentQuantity?: number;
  rewardAmount?: number;
}

interface Campaign {
  campaignId: string;
  campaignName?: string;
  brandName?: string;
  campaignCategory?: string;
  representativeImages?: CampaignImage[];
  contentChannels?: string[];
  contentChannel?: string;
  rewardAmount?: number;
  recruitmentPeriod?: RecruitmentPeriod;
  numberOfRecruits?: number;
  providedValueDescription?: string;
  productName?: string;
  productIntroduction?: string;
  provisionMethod?: string;
  additionalNotes?: string;
  visitAddress?: string;
  visitLatitude?: number | null;
  visitLongitude?: number | null;
  contentInsertLink?: string;
  announcementStartDate?: string;
  announcementEndDate?: string;
  visitStartDate?: string;
  visitEndDate?: string;
  uploadDeadlineDays?: number;
  discussSchedule?: boolean;
  contentWritingRequirements?: string;
  secondarySubmissions?: { type: string; quantity: number; note?: string }[];
  submissionPath?: string;
  secondarySubmissionGuide?: string;
  contentTypes?: string[];
  publishingConditions?: PublishingCondition[];
  recommendedKeywords?: string;
  status?: string;
}

/* ─── Constants ─── */
const CATEGORY_LABELS: Record<string, string> = {
  accommodation: '숙박',
  restaurant: '음식점',
  cafe: '카페',
  tour: '투어',
  ticket: '티켓',
  shopping: '쇼핑',
  wellness: '웰니스',
  other: '기타',
};

const STATUS_STYLES: Record<string, { labelKey: string; bg: string; text: string }> = {
  recruiting: { labelKey: 'campaignDetail.statusRecruiting', bg: 'bg-green-100', text: 'text-green-700' },
  in_progress: { labelKey: 'campaignDetail.statusInProgress', bg: 'bg-blue-100', text: 'text-blue-700' },
  completed: { labelKey: 'campaignDetail.statusCompleted', bg: 'bg-gray-100', text: 'text-gray-600' },
  closed: { labelKey: 'campaignDetail.statusClosed', bg: 'bg-red-100', text: 'text-red-700' },
  draft: { labelKey: 'campaignDetail.statusDraft', bg: 'bg-yellow-100', text: 'text-yellow-700' },
};

const BRAND_GRADIENTS = [
  'from-rose-500 to-orange-400',
  'from-violet-600 to-indigo-400',
  'from-emerald-500 to-teal-400',
  'from-sky-500 to-cyan-400',
  'from-fuchsia-500 to-pink-400',
  'from-amber-500 to-yellow-400',
  'from-blue-600 to-violet-500',
  'from-teal-500 to-emerald-400',
];

const formatDate = (d?: string) => (d ? d.slice(0, 10) : '');

const getChannels = (c: Campaign): string[] =>
  (c.contentChannels?.length ? c.contentChannels : c.contentChannel ? [c.contentChannel] : []).filter(Boolean);

const gradientForBrand = (name?: string) => {
  let hash = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return BRAND_GRADIENTS[Math.abs(hash) % BRAND_GRADIENTS.length];
};

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */
export const PublicCampaignDetails: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { isAuthenticated, user } = useAuthStore();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [appliedMatchId, setAppliedMatchId] = useState<string | null>(null);
  const [appliedMatchStatus, setAppliedMatchStatus] = useState<string | null>(null);
  const [canceledMatchId, setCanceledMatchId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [consentPortrait, setConsentPortrait] = useState(false);
  const [consentCampaignTerms, setConsentCampaignTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentDetailModal, setConsentDetailModal] = useState<string | null>(null);
  const allConsentsChecked = consentPortrait && consentCampaignTerms && consentPrivacy;

  // Apply modal state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyMissions, setApplyMissions] = useState<string[]>([]);
  const [applyInstagram, setApplyInstagram] = useState('');
  const [applyBlog, setApplyBlog] = useState('');
  const [applyYoutube, setApplyYoutube] = useState('');
  const [applyVisitStart, setApplyVisitStart] = useState('');
  const [applyVisitEnd, setApplyVisitEnd] = useState('');
  const [applyAdults, setApplyAdults] = useState(1);
  const [applyChildren, setApplyChildren] = useState(0);
  const [applyInfants, setApplyInfants] = useState(0);
  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (!campaignId || fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const response = await API.graphql({
          query: GET_PUBLIC_CAMPAIGN,
          variables: { campaignId },
          authMode: 'AWS_IAM',
        });
        const data = (response as any).data?.getPublicCampaign;
        if (data) {
          setCampaign(data);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to fetch campaign:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  // Check if user already applied to this campaign
  useEffect(() => {
    if (!isAuthenticated || !campaignId || user?.userType !== 'influencer') return;
    const influencerId = user?.influencerId;
    const userId = user?.userId;
    if (!influencerId && !userId) return;
    (async () => {
      try {
        // Try filtering by influencerId first, fall back to userId
        const filters = influencerId
          ? { influencerId, campaignId }
          : { userId, campaignId };
        const res = await graphqlApi.getMatches(filters as any);
        if (res.success && (res.data?.matches?.length ?? 0) > 0) {
          const activeMatch = res.data!.matches!.find((m: any) => m.status !== 'canceled');
          if (activeMatch) {
            setApplied(true);
            setAppliedMatchId(activeMatch.matchId);
            setAppliedMatchStatus(activeMatch.status);
          } else {
            // Check for canceled match that can be reactivated
            const cancelled = res.data!.matches!.find((m: any) => m.status === 'canceled');
            if (cancelled) setCanceledMatchId(cancelled.matchId);
          }
        }
      } catch { /* ignore — user may not have matches permission yet */ }
    })();
  }, [isAuthenticated, user?.influencerId, user?.userId, user?.userType, campaignId]);

  /* ── Derived ── */
  const channels = campaign ? getChannels(campaign) : [];
  const categoryLabel = campaign?.campaignCategory ? t(`categories.${campaign.campaignCategory}`) : '';
  const gradient = gradientForBrand(campaign?.brandName);
  const brandInitial = (campaign?.brandName || 'B').charAt(0).toUpperCase();
  const heroImg = campaign?.representativeImages?.[0]?.fileUrl;
  const statusMeta = STATUS_STYLES[campaign?.status || ''];

  const keywords: string[] = campaign?.recommendedKeywords
    ? campaign.recommendedKeywords.split(',').map((k: string) => k.trim()).filter(Boolean)
    : [];

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNavBar variant="dark" maxWidth="max-w-5xl" />
        <div className="flex items-center justify-center py-40">
          <Spinner size="large" />
        </div>
      </div>
    );
  }

  /* ── Error / Not Found ── */
  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNavBar variant="dark" maxWidth="max-w-5xl" />
        <div className="max-w-5xl mx-auto px-4 py-20 text-center">
          <div className="text-5xl mb-4">😢</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('campaignDetail.notFound')}</h2>
          <p className="text-gray-500 mb-6">{t('campaignDetail.notFoundDesc')}</p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors"
          >
{t('campaignDetail.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  const openApplyModal = async () => {
    // Pre-fill channel info from influencer profile
    if (user?.userId) {
      try {
        const res = await graphqlApi.getInfluencerByUserId(user.userId);
        if (res.success && res.data) {
          const h = res.data.socialMediaHandles || {};
          setApplyInstagram(h.instagram?.account || '');
          setApplyBlog(h.naverblog?.account || '');
          setApplyYoutube(h.youtube?.account || '');
        }
      } catch { /* ignore */ }
    }
    // Pre-select available missions
    const pc = campaign?.publishingConditions as any;
    const missions: string[] = [];
    if (pc?.instagram?.conditions?.length > 0) missions.push('instagram');
    if (pc?.blog?.conditions?.length > 0) missions.push('blog');
    if (pc?.youtube?.conditions?.length > 0) missions.push('youtube');
    setApplyMissions(missions);
    // Pre-fill visit dates from campaign
    if (campaign?.visitStartDate) setApplyVisitStart(campaign.visitStartDate.slice(0, 10));
    if (campaign?.visitEndDate) setApplyVisitEnd(campaign.visitEndDate.slice(0, 10));
    setShowApplyModal(true);
  };

  const handleApplySubmit = async () => {
    if (!allConsentsChecked) { toast.error(t('campaignDetail.consentRequired')); return; }
    if (applyMissions.length === 0) { toast.error(t('campaignDetail.applyModal.selectMission')); return; }
    // Validate selected missions have channel info filled
    const channelMap: Record<string, string> = { instagram: applyInstagram, blog: applyBlog, youtube: applyYoutube };
    const emptyChannels = applyMissions.filter(m => !channelMap[m]?.trim());
    if (emptyChannels.length > 0) {
      toast.error(t('campaignDetail.applyModal.channelRequired', '선택한 미션의 채널정보를 등록해주세요.'));
      return;
    }
    // Validate visit dates within campaign period
    if (applyVisitStart && applyVisitEnd && campaign) {
      const campStart = campaign.visitStartDate?.slice(0, 10);
      const campEnd = campaign.visitEndDate?.slice(0, 10);
      if (campStart && applyVisitStart < campStart) { toast.error(t('campaignDetail.applyModal.visitStartAfter', { date: campStart })); return; }
      if (campEnd && applyVisitEnd > campEnd) { toast.error(t('campaignDetail.applyModal.visitEndBefore', { date: campEnd })); return; }
      if (applyVisitStart > applyVisitEnd) { toast.error(t('campaignDetail.applyModal.visitStartBeforeEnd')); return; }
    }
    if (!campaignId || !user?.influencerId) {
      toast.error(t('campaignDetail.profileRequired'));
      navigate('/my-profile/profile');
      return;
    }
    setApplying(true);
    try {
      let result;
      if (canceledMatchId) {
        result = await graphqlApi.updateMatch(canceledMatchId, {
          status: 'applied',
          selectedMissions: applyMissions,
          channelInfo: {
            instagram: { account: applyInstagram || undefined },
            naverblog: { account: applyBlog || undefined },
            youtube: { account: applyYoutube || undefined },
          },
          visitInfo: {
            startDate: applyVisitStart || undefined,
            endDate: applyVisitEnd || undefined,
            adults: applyAdults,
            children: applyChildren,
            infants: applyInfants,
          },
          campaignConsents: {
            portrait: consentPortrait,
            campaignTerms: consentCampaignTerms,
            privacy: consentPrivacy,
          },
        } as any);
        if (result.success) {
          setApplied(true);
          setAppliedMatchId(canceledMatchId);
          setAppliedMatchStatus('applied');
          setCanceledMatchId(null);
          toast.success(t('campaignDetail.reapplySuccess'));
        }
      } else {
        result = await graphqlApi.createMatch({
          campaignId,
          influencerId: user.influencerId,
          coverLetter: t('campaignDetail.defaultCoverLetter'),
          selectedMissions: applyMissions,
          channelInfo: {
            instagram: { account: applyInstagram || undefined },
            naverblog: { account: applyBlog || undefined },
            youtube: { account: applyYoutube || undefined },
          },
          visitInfo: {
            startDate: applyVisitStart || undefined,
            endDate: applyVisitEnd || undefined,
            adults: applyAdults,
            children: applyChildren,
            infants: applyInfants,
          },
          campaignConsents: {
            portrait: consentPortrait,
            campaignTerms: consentCampaignTerms,
            privacy: consentPrivacy,
          },
        } as any);
        if (result.success) {
          setApplied(true);
          setAppliedMatchId(result.data?.matchId || null);
          setAppliedMatchStatus('applied');
          toast.success(t('campaignDetail.applySuccess'));
        }
      }
      if (result && !result.success) {
        toast.error(result.error || t('campaignDetail.applyFailed'));
      }
    } catch (err: any) {
      toast.error(err.message || t('campaignDetail.applyFailed'));
    } finally {
      setApplying(false);
      setShowApplyModal(false);
    }
  };

  /* ── Main Render ── */
  const isRecruitmentClosed = (() => {
    if (!campaign) return false;
    // Check campaign status
    if (['paused', 'closed', 'completed', 'cancelled'].includes(campaign.status || '')) return true;
    // Check recruitment period expired
    if (campaign.recruitmentPeriod?.alwaysRecruiting) return false;
    if (campaign.recruitmentPeriod?.endDate) {
      return new Date(campaign.recruitmentPeriod.endDate) < new Date();
    }
    return false;
  })();
  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" maxWidth="max-w-5xl" />

      {/* Breadcrumb */}
      <div className="max-w-5xl mx-auto px-4 py-3">
        <nav className="text-sm text-gray-500">
          <Link to="/" className="hover:text-blue-600 transition-colors">{t('campaignDetail.home')}</Link>
          <span className="mx-1.5">&gt;</span>
          <span>{t('campaignDetail.campaign')}</span>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{campaign.campaignName || ''}</span>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ── Left Column (2/3) ── */}
          <div className="lg:w-2/3 space-y-6">
            {/* Hero Image */}
            {heroImg ? (
              <img
                src={heroImg}
                alt={campaign.campaignName || ''}
                className="w-full h-80 object-cover rounded-xl"
              />
            ) : (
              <div className={`w-full h-80 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                <span className="text-white/50 text-7xl font-black">{brandInitial}</span>
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900">{campaign.campaignName || ''}</h1>

            {/* Brand */}
            {campaign.brandName && (
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                  <span className="text-white text-xs font-bold">{brandInitial}</span>
                </div>
                <span className="text-sm font-medium text-gray-700">{campaign.brandName}</span>
              </div>
            )}

            {/* ── Section 1: 캠페인 기본정보 ── */}
            {(campaign.brandName || campaign.campaignName || campaign.campaignCategory) && (
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
                <h3 className="text-base font-bold text-gray-900">{t('campaignDetail.campaignBasicInfo')}</h3>
                {campaign.brandName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.brandNameLabel')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.brandName}</span>
                  </div>
                )}
                {campaign.campaignName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.campaignNameLabel')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.campaignName}</span>
                  </div>
                )}
                {categoryLabel && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm text-gray-500">{t('campaignDetail.categoryLabel')}</span>
                    <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full">
                      {categoryLabel}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Section 2: 제공 제품·서비스 정보 ── */}
            {(campaign.productName || campaign.provisionMethod || campaign.visitAddress || campaign.productIntroduction || campaign.providedValueDescription) && (
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
                <h3 className="text-base font-bold text-gray-900">{t('campaignDetail.productServiceInfo')}</h3>
                {campaign.productIntroduction && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('campaignDetail.productIntro')}</span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.productIntroduction}</p>
                  </div>
                )}
                {campaign.productName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.productName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.productName}</span>
                  </div>
                )}
                {campaign.providedValueDescription && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('campaignDetail.providedValue')}</span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.providedValueDescription}</p>
                  </div>
                )}
                {campaign.provisionMethod && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.provisionMethod')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.provisionMethod}</span>
                  </div>
                )}
                {campaign.additionalNotes && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('campaignDetail.visitNotes')}</span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.additionalNotes}</p>
                  </div>
                )}
                {campaign.visitAddress && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.visitAddress')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.visitAddress}</span>
                  </div>
                )}
                {typeof campaign.visitLatitude === 'number' && typeof campaign.visitLongitude === 'number' && (
                  <div className="py-2">
                    <AddressMapView
                      latitude={campaign.visitLatitude}
                      longitude={campaign.visitLongitude}
                      ariaLabel={campaign.visitAddress}
                    />
                  </div>
                )}
              </div>
            )}

            {/* ── Section 3: 캠페인 미션 ── */}
            {(campaign.rewardAmount || campaign.contentWritingRequirements || (campaign as any).publishingConditions || channels.length > 0 || keywords.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
                <h3 className="text-base font-bold text-gray-900">{t('campaignDetail.campaignMission')}</h3>

                {/* 모집인원 */}
                {campaign.numberOfRecruits != null && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.numberOfRecruits')}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {t('campaignDetail.recruitsCount', { count: campaign.numberOfRecruits })}
                    </span>
                  </div>
                )}

                {/* 콘텐츠 채널 */}
                {channels.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.contentChannels')}</span>
                    <div className="flex flex-wrap gap-3">
                      {channels.map((ch, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                        >
                          <ChannelIcon channel={ch} className="w-5 h-5" />
                          <span className="text-sm font-medium text-gray-700 capitalize">{ch}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {campaign.rewardAmount != null && campaign.rewardAmount > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">{t('campaignDetail.settlementAmount')}</span>
                      <span className="text-sm font-bold text-blue-600">${campaign.rewardAmount.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {/* Publishing Conditions — V2 with legacy fallback */}
                {(() => {
                  const pc = (campaign as any).publishingConditions;
                  const igConds = pc?.instagram?.conditions || [];
                  const igCollabId = pc?.instagram?.collaboratorId || (campaign as any).instagramCollaboratorId;
                  const blogConds = pc?.blog?.conditions || [];
                  const blogLink = pc?.blog?.insertLink || (campaign as any).blogInsertLink;
                  const ytConds = pc?.youtube?.conditions || [];
                  const ytLink = pc?.youtube?.insertLink || (campaign as any).youtubeInsertLink;
                  return (
                    <>
                      {igConds.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.instagramConditions')}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {igConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-pink-50 text-pink-700 text-xs font-medium rounded-full">
                                {c.item === 'reels' ? t('campaignDetail.reels') : c.item === 'story' ? t('campaignDetail.story') : t('campaignDetail.postFeed')} × {c.quantity}
                              </span>
                            ))}
                          </div>
                          {igCollabId && <p className="mt-1.5 text-xs text-gray-500">공동작업자 ID: <span className="text-gray-700 font-medium">{igCollabId}</span></p>}
                        </div>
                      )}
                      {blogConds.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.blogConditions')}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {blogConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">{t('campaignDetail.blogPost')} × {c.quantity}</span>
                            ))}
                          </div>
                          {blogLink && <p className="mt-1.5 text-xs text-gray-500">콘텐츠 내 삽입링크: <a href={blogLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{blogLink}</a></p>}
                        </div>
                      )}
                      {ytConds.length > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.youtubeConditions')}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {ytConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">
                                {c.item === 'video' ? t('campaignDetail.video') : t('campaignDetail.shorts')} × {c.quantity}
                              </span>
                            ))}
                          </div>
                          {ytLink && <p className="mt-1.5 text-xs text-gray-500">콘텐츠 내 삽입링크: <a href={ytLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{ytLink}</a></p>}
                        </div>
                      )}
                    </>
                  );
                })()}

                {campaign.contentInsertLink && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.contentInsertLink')}</span>
                    <a href={campaign.contentInsertLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-[200px]">{campaign.contentInsertLink}</a>
                  </div>
                )}

                {/* Schedule */}
                {(campaign.announcementStartDate || campaign.visitStartDate || campaign.uploadDeadlineDays) && (
                  <div className="space-y-1.5">
                    <span className="text-xs font-semibold text-gray-400 block">{t('campaignDetail.schedule')}</span>
                    {(campaign as any).alwaysOpening ? (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('campaignDetail.announcementDate')}</span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">상시선정</span>
                      </div>
                    ) : campaign.announcementStartDate && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('campaignDetail.announcementDate')}</span>
                        <span className="text-gray-700">{formatDate(campaign.announcementStartDate)}{campaign.announcementEndDate ? ` ~ ${formatDate(campaign.announcementEndDate)}` : ''}</span>
                      </div>
                    )}
                    {campaign.visitStartDate && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('campaignDetail.visitPeriod')}</span>
                        <span className="text-gray-700">{formatDate(campaign.visitStartDate)}{campaign.visitEndDate ? ` ~ ${formatDate(campaign.visitEndDate)}` : ''}</span>
                      </div>
                    )}
                    {campaign.uploadDeadlineDays && (
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{t('campaignDetail.uploadDeadline')}</span>
                        <span className="text-gray-700">{t('campaignDetail.uploadDeadlineDays', { days: campaign.uploadDeadlineDays })}</span>
                      </div>
                    )}
                    {campaign.discussSchedule && (
                      <span className="inline-block px-2 py-0.5 bg-yellow-50 text-yellow-700 text-[10px] font-medium rounded">{t('campaignDetail.discussSchedule')}</span>
                    )}
                  </div>
                )}

                {campaign.contentWritingRequirements && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('campaignDetail.contentGuide')}</span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.contentWritingRequirements}</p>
                  </div>
                )}

                {(campaign.secondarySubmissions?.length ?? 0) > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.secondarySubmissions')}</span>
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50"><th className="px-2 py-1 text-left text-gray-500">{t('campaignDetail.itemColumn')}</th><th className="px-2 py-1 text-left text-gray-500">{t('campaignDetail.quantityColumn')}</th><th className="px-2 py-1 text-left text-gray-500">{t('campaignDetail.noteColumn')}</th></tr></thead>
                      <tbody>
                        {campaign.secondarySubmissions?.map((s: any, i: number) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="px-2 py-1">{s.type === 'video' ? t('campaignDetail.videoType') : t('campaignDetail.photoType')}</td>
                            <td className="px-2 py-1">{s.quantity} {s.type === 'video' ? t('campaignDetail.minutes') : t('campaignDetail.pieces')}</td>
                            <td className="px-2 py-1">{s.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {campaign.submissionPath && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('campaignDetail.submissionPath')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.submissionPath}</span>
                  </div>
                )}

                {campaign.secondarySubmissionGuide && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('campaignDetail.secondaryGuide')}</span>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{campaign.secondarySubmissionGuide}</p>
                  </div>
                )}

                {/* 추천 키워드 — nested inside Mission section to match the register form's Section 3 order */}
                {keywords.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 block mb-2">{t('campaignDetail.recommendedKeywords')}</span>
                    <div className="flex flex-wrap gap-2">
                      {keywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full"
                        >
                          #{kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Content Channels and Recommended Keywords are now nested inside
                Section 3 (Campaign Mission) to match the register form's
                Section 3 order. The standalone blocks were removed. */}
          </div>

          {/* ── Right Column (1/3) — Sticky Sidebar ── */}
          <div className="lg:w-1/3">
            <div className="sticky top-20 space-y-4">
              {/* Campaign Info Card */}
              <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
                <h3 className="text-base font-bold text-gray-900">{t('campaignDetail.campaignInfo')}</h3>

                {/* Category */}
                {categoryLabel && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('campaignDetail.categoryLabel')}</span>
                    <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full">
                      {categoryLabel}
                    </span>
                  </div>
                )}

                {/* Recruitment Period */}
                {campaign.recruitmentPeriod && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">모집기간</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatDate(campaign.recruitmentPeriod.startDate)}
                      {campaign.recruitmentPeriod.endDate &&
                        ` ~ ${formatDate(campaign.recruitmentPeriod.endDate)}`}
                    </span>
                  </div>
                )}

                {/* Number of Recruits */}
                {campaign.numberOfRecruits != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('campaignDetail.numberOfRecruits')}</span>
                    <span className="text-sm font-medium text-gray-900">
{t('campaignDetail.recruitsCount', { count: campaign.numberOfRecruits })}
                    </span>
                  </div>
                )}

                {/* Reward Amount */}
                {campaign.rewardAmount != null && campaign.rewardAmount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('campaignDetail.reward')}</span>
                    <span className="text-sm font-bold text-blue-600">
                      ${campaign.rewardAmount.toLocaleString()}
                    </span>
                  </div>
                )}

                {/* Status */}
                {campaign.status && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{t('campaignDetail.status')}</span>
                    <span
                      className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                        statusMeta ? `${statusMeta.bg} ${statusMeta.text}` : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {statusMeta ? t(statusMeta.labelKey) : campaign.status}
                    </span>
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              {!isAuthenticated ? (
                <Link
                  to="/login"
                  onClick={() => storeRedirectIntent(`/campaign/${campaignId}`, 'apply')}
                  className="block w-full text-center py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-colors shadow-sm hover:shadow-md"
                >
{t('campaignDetail.loginToApply')}
                </Link>
              ) : user?.userType !== 'influencer' ? null : applied ? (
                <div className="space-y-2">
                  <div className="w-full text-center py-3 rounded-xl bg-green-100 text-green-700 font-semibold">
{t('campaignDetail.applied')}
                  </div>
                  {['applied', 'shortlisted', 'requested'].includes(appliedMatchStatus || '') && appliedMatchId && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(t('campaignDetail.confirmCancel'))) return;
                        setCancelling(true);
                        try {
                          const res = await graphqlApi.withdrawMatch(appliedMatchId);
                          if (res.success) {
                            setApplied(false);
                            setCanceledMatchId(appliedMatchId);
                            setAppliedMatchId(null);
                            setAppliedMatchStatus(null);
                            toast.success(t('campaignDetail.cancelSuccess'));
                          } else {
                            // Backend guard: see handleWithdrawMatch. Match
                            // the error by substring so minor wording
                            // changes still land on the right toast.
                            const msg = (res.error || '').toLowerCase();
                            if (msg.includes('content has already been submitted')) {
                              toast.error(t('campaignDetail.cancelContentSubmitted'));
                            } else {
                              toast.error(t('campaignDetail.cancelFailed'));
                            }
                          }
                        } catch { toast.error(t('campaignDetail.cancelFailed')); }
                        finally { setCancelling(false); }
                      }}
                      disabled={cancelling}
                      className="w-full text-center py-2.5 rounded-xl border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      {cancelling ? t('campaignDetail.cancelling') : t('campaignDetail.cancelApplication')}
                    </button>
                  )}
                </div>
              ) : isRecruitmentClosed ? (
                <div className="w-full text-center py-3 rounded-xl bg-gray-100 text-gray-400 font-medium">
                  {t('campaignDetail.recruitmentExpired', '모집기간이 만료된 캠페인입니다')}
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (!campaignId || !user?.influencerId) {
                      toast.error(t('campaignDetail.profileRequired'));
                      navigate('/my-profile/profile');
                      return;
                    }
                    openApplyModal();
                  }}
                  disabled={applying}
                  className={`w-full text-center py-3 rounded-xl font-semibold transition-colors shadow-sm hover:shadow-md ${
                    applying ? 'bg-blue-400 cursor-not-allowed text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {applying ? t('campaignDetail.applying') : t('campaignDetail.apply')}
                </button>
              )}

              <Link
                to="/discovery"
                className="block w-full text-center py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
{t('campaignDetail.backToList')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Apply Modal */}
    {showApplyModal && campaign && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowApplyModal(false)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-5" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-bold text-gray-900">{t('campaignDetail.applyModal.title')}</h3>
          <hr className="border-gray-200" />

          {/* Section 1: 캠페인 미션 참여 선택 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('campaignDetail.applyModal.missionTitle')}</h4>
            <div className="space-y-3">
              {(() => {
                const pc = (campaign.publishingConditions as any) || {};
                const ITEM_LABEL: Record<string, string> = { reels: '릴스', story: '스토리', post: '포스트/피드', feed: '피드', blogPost: '포스팅', video: '영상', shorts: '쇼츠' };
                const channels = [
                  { key: 'instagram', label: '인스타그램', icon: 'instagram', data: pc.instagram },
                  { key: 'blog', label: '블로그', icon: 'blog', data: pc.blog },
                  { key: 'youtube', label: '유튜브', icon: 'youtube', data: pc.youtube },
                ].filter(ch => ch.data?.conditions?.length > 0);
                return channels.map(ch => (
                  <label key={ch.key} className="flex items-start gap-2.5 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input type="checkbox" checked={applyMissions.includes(ch.key)}
                      onChange={() => setApplyMissions(prev => prev.includes(ch.key) ? prev.filter(m => m !== ch.key) : [...prev, ch.key])}
                      className="w-5 h-5 mt-0.5 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500" />
                    <div className="flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                        <ChannelIcon channel={ch.icon} className="w-4 h-4" />
                        {ch.label} {t('campaignDetail.applyModal.missionConditions')}
                      </span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {ch.data.conditions.map((cond: any, idx: number) => (
                          <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] rounded">
                            {ITEM_LABEL[cond.item] || cond.item} {cond.quantity}{t('campaignDetail.applyModal.items')}
                          </span>
                        ))}
                      </div>
                      {ch.data.collaboratorId && <p className="mt-1 text-[11px] text-gray-400">{t('campaignDetail.applyModal.collaborator')}: {ch.data.collaboratorId}</p>}
                      {ch.data.insertLink && <p className="mt-1 text-[11px] text-gray-400">{t('campaignDetail.applyModal.insertLink')}: {ch.data.insertLink}</p>}
                    </div>
                  </label>
                ));
              })()}
            </div>
          </div>
          <hr className="border-gray-200" />

          {/* Section 2: 나의 채널정보 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('campaignDetail.applyModal.channelTitle')}</h4>
            <div className="space-y-2">
              <div className={`flex items-center gap-2 ${!applyMissions.includes('instagram') ? 'opacity-40 pointer-events-none' : ''}`}>
                <ChannelIcon channel="instagram" className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs text-gray-500 w-20">{t('campaignDetail.applyModal.instagram')}</span>
                {applyInstagram ? (
                  <input type="text" value={applyInstagram} onChange={e => setApplyInstagram(e.target.value)} placeholder="@instagram_id"
                    disabled={!applyMissions.includes('instagram')}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
                ) : (
                  <button onClick={() => { setShowApplyModal(false); navigate('/my-profile/profile'); }}
                    className="flex-1 px-2 py-1.5 text-xs text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 text-center">
                    {t('campaignDetail.applyModal.registerChannel', 'SNS 채널정보 등록')}
                  </button>
                )}
              </div>
              <div className={`flex items-center gap-2 ${!applyMissions.includes('blog') ? 'opacity-40 pointer-events-none' : ''}`}>
                <ChannelIcon channel="blog" className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs text-gray-500 w-20">{t('campaignDetail.applyModal.blog')}</span>
                {applyBlog ? (
                  <input type="text" value={applyBlog} onChange={e => setApplyBlog(e.target.value)} placeholder="blog_id"
                    disabled={!applyMissions.includes('blog')}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
                ) : (
                  <button onClick={() => { setShowApplyModal(false); navigate('/my-profile/profile'); }}
                    className="flex-1 px-2 py-1.5 text-xs text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 text-center">
                    {t('campaignDetail.applyModal.registerChannel', 'SNS 채널정보 등록')}
                  </button>
                )}
              </div>
              <div className={`flex items-center gap-2 ${!applyMissions.includes('youtube') ? 'opacity-40 pointer-events-none' : ''}`}>
                <ChannelIcon channel="youtube" className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs text-gray-500 w-20">{t('campaignDetail.applyModal.youtube')}</span>
                {applyYoutube ? (
                  <input type="text" value={applyYoutube} onChange={e => setApplyYoutube(e.target.value)} placeholder="@youtube_id"
                    disabled={!applyMissions.includes('youtube')}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
                ) : (
                  <button onClick={() => { setShowApplyModal(false); navigate('/my-profile/profile'); }}
                    className="flex-1 px-2 py-1.5 text-xs text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 text-center">
                    {t('campaignDetail.applyModal.registerChannel', 'SNS 채널정보 등록')}
                  </button>
                )}
              </div>
            </div>
          </div>
          <hr className="border-gray-200" />

          {/* Section 3: 방문정보 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('campaignDetail.applyModal.visitTitle')}</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{t('campaignDetail.applyModal.visitDate')} <span className="text-gray-400">* {t('campaignDetail.applyModal.visitDateHint')}</span></label>
                <div className="text-[10px] text-gray-400 mb-1">{t('campaignDetail.applyModal.visitPeriod')}: {campaign.visitStartDate?.slice(0, 10) || '-'} ~ {campaign.visitEndDate?.slice(0, 10) || '-'}</div>
                <div className="flex items-center gap-2">
                  <input type="date" value={applyVisitStart} onChange={e => setApplyVisitStart(e.target.value)}
                    min={campaign.visitStartDate?.slice(0, 10)} max={campaign.visitEndDate?.slice(0, 10)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                  <span className="text-gray-400">~</span>
                  <input type="date" value={applyVisitEnd} onChange={e => setApplyVisitEnd(e.target.value)}
                    min={applyVisitStart || campaign.visitStartDate?.slice(0, 10)} max={campaign.visitEndDate?.slice(0, 10)}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">{t('campaignDetail.applyModal.visitors')}</label>
                <div className="flex gap-4 flex-wrap">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{t('campaignDetail.applyModal.adults')}</span>
                    <input type="number" min={0} value={applyAdults} onChange={e => setApplyAdults(parseInt(e.target.value) || 0)} className="w-14 px-2 py-1 text-sm border border-gray-300 rounded text-center" />
                    <span className="text-xs text-gray-400">{t('campaignDetail.applyModal.persons')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{t('campaignDetail.applyModal.children')}</span>
                    <input type="number" min={0} value={applyChildren} onChange={e => setApplyChildren(parseInt(e.target.value) || 0)} className="w-14 px-2 py-1 text-sm border border-gray-300 rounded text-center" />
                    <span className="text-[10px] text-gray-400">{t('campaignDetail.applyModal.childrenAge')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{t('campaignDetail.applyModal.infants')}</span>
                    <input type="number" min={0} value={applyInfants} onChange={e => setApplyInfants(parseInt(e.target.value) || 0)} className="w-14 px-2 py-1 text-sm border border-gray-300 rounded text-center" />
                    <span className="text-[10px] text-gray-400">{t('campaignDetail.applyModal.infantsAge')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr className="border-gray-200" />

          {/* Section 4: 캠페인 이용 동의 */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('campaignDetail.applyModal.consentTitle')}</h4>
            <div className="space-y-2">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={consentPortrait} onChange={e => setConsentPortrait(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-xs text-gray-700 leading-relaxed">
                  {t('campaignDetail.consentPortrait')}
                  <button type="button" onClick={() => setConsentDetailModal('portrait')} className="ml-1 text-blue-600 hover:underline">{t('campaignDetail.consentViewDetail')}</button>
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={consentCampaignTerms} onChange={e => setConsentCampaignTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-xs text-gray-700 leading-relaxed">
                  {t('campaignDetail.consentCampaignTerms')}
                  <button type="button" onClick={() => setConsentDetailModal('campaignTerms')} className="ml-1 text-blue-600 hover:underline">{t('campaignDetail.consentViewDetail')}</button>
                </span>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={consentPrivacy} onChange={e => setConsentPrivacy(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-xs text-gray-700 leading-relaxed">
                  {t('campaignDetail.consentPrivacy')}
                  <button type="button" onClick={() => setConsentDetailModal('privacy')} className="ml-1 text-blue-600 hover:underline">{t('campaignDetail.consentViewDetail')}</button>
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button onClick={() => setShowApplyModal(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">{t('campaignDetail.applyModal.cancel')}</button>
            <button onClick={handleApplySubmit} disabled={applying || !allConsentsChecked || applyMissions.length === 0}
              className="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50">
              {applying ? t('campaignDetail.applyModal.submitting') : t('campaignDetail.applyModal.submit')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Consent Detail Modal */}
    {consentDetailModal && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setConsentDetailModal(null)}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
          {consentDetailModal === 'portrait' && campaign && (
            <>
              <h3 className="text-lg font-bold text-gray-900">초상권 활용 및 2차 제출 동의</h3>
              <hr />
              <div className="text-sm text-gray-700 space-y-4 leading-relaxed">
                {/* 콘텐츠 작성 가이드 */}
                <div>
                  <p className="font-semibold text-gray-900 mb-1">콘텐츠 작성 가이드</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 whitespace-pre-line">{campaign.contentWritingRequirements || '등록된 가이드가 없습니다.'}</div>
                </div>

                {/* 2차 활용 제출 항목 */}
                <div>
                  <p className="font-semibold text-gray-900 mb-1">2차 활용 제출 항목</p>
                  {campaign.secondarySubmissions && campaign.secondarySubmissions.length > 0 ? (
                    <table className="w-full text-xs border border-gray-200 rounded">
                      <thead><tr className="bg-gray-50 text-gray-500"><th className="px-2 py-1 text-left">#</th><th className="px-2 py-1 text-left">항목</th><th className="px-2 py-1 text-left">수량/분량</th><th className="px-2 py-1 text-left">비고</th></tr></thead>
                      <tbody>
                        {campaign.secondarySubmissions.map((s: any, i: number) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-2 py-1">#{i + 1}</td>
                            <td className="px-2 py-1">{s.type === 'video' ? '영상' : '사진'}</td>
                            <td className="px-2 py-1">{s.quantity}{s.type === 'video' ? '분' : '장'}</td>
                            <td className="px-2 py-1 text-gray-500">{s.note || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-gray-400">등록된 항목이 없습니다.</p>
                  )}
                </div>

                {/* 제출경로 */}
                <div>
                  <p className="font-semibold text-gray-900 mb-1">제출경로</p>
                  {campaign.submissionPath ? (
                    <a href={campaign.submissionPath} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline break-all">{campaign.submissionPath}</a>
                  ) : (
                    <p className="text-xs text-gray-400">등록된 제출경로가 없습니다.</p>
                  )}
                </div>

                {/* 2차 활용 제출 요청 가이드 */}
                <div>
                  <p className="font-semibold text-gray-900 mb-1">2차 활용 제출 요청 가이드</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 whitespace-pre-line">{campaign.secondarySubmissionGuide || '등록된 가이드가 없습니다.'}</div>
                </div>
              </div>
            </>
          )}
          {consentDetailModal === 'campaignTerms' && (
            <>
              <h3 className="text-lg font-bold text-gray-900">캠페인 유의사항, 콘텐츠 제3자 제공, 저작물 이용 동의</h3>
              <hr />
              <div className="text-sm text-gray-700 space-y-3 leading-relaxed">
                <p>• 정당한 사유 없이 콘텐츠 등록 기간 내 콘텐츠를 작성하지 않을 경우 제공상품 또는 용역의 대가를 환불해야 하며, 관련 법 조항(형법 제347조)에 따라 법적 처벌 대상이 될 수 있습니다.</p>
                <p>• 등록한 콘텐츠의 유지 기간(6개월) 미준수 시 제공 내역에 대한 비용이 청구될 수 있습니다.</p>
                <p>• 등록한 콘텐츠는 홍보나 필요에 의해 사용될 수 있으며, 광고주가 판매 사이트에 콘텐츠 활용을 할 수 있습니다. (광고 등 2차적 저작물 활용)</p>
                <p>• 제공 내역은 타인에게 양도 및 판매, 교환을 허용하지 않습니다.</p>
                <p>• 키워드 챌린지 캠페인은 최소 30일 동안 유지되어야 하며, 유지 기간 미준수 시 콘텐츠 재개 요청이 있을 수 있습니다.</p>
                <p className="text-xs text-gray-500 mt-2">※ 회원님께서는 위 내용에 동의하지 않을 수 있으며, 이를 거부할 경우 해당 캠페인 참여가 제한됩니다.</p>
              </div>
            </>
          )}
          {consentDetailModal === 'privacy' && (
            <>
              <h3 className="text-lg font-bold text-gray-900">개인정보 제3자 제공 동의</h3>
              <hr />
              <div className="text-sm text-gray-700 space-y-3 leading-relaxed">
                <p>원활한 캠페인 서비스 제공을 위해 최소한의 범주 내에서 아래와 같이 개인정보를 제공합니다. 회원님께서는 제3자 제공에 동의하지 않으실 수 있으며, 이를 거부할 경우 해당 캠페인 참여가 제한됩니다.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-gray-200 rounded">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="px-3 py-2 text-left border-b border-gray-200">제공받는 자</th>
                        <th className="px-3 py-2 text-left border-b border-gray-200">제공목적</th>
                        <th className="px-3 py-2 text-left border-b border-gray-200">제공 항목</th>
                        <th className="px-3 py-2 text-left border-b border-gray-200">보유 및 이용기간</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-3 py-2 align-top">{campaign?.brandName || '캠페인 호스트 브랜드'}</td>
                        <td className="px-3 py-2 align-top">캠페인 선정 및 진행, 본인인증</td>
                        <td className="px-3 py-2 align-top">회원정보 (이름, 생년월일, 성별, 휴대전화번호, 캠페인 참여를 위한 추가 개인정보)</td>
                        <td className="px-3 py-2 align-top">캠페인 종료 후 1개월</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          <button onClick={() => setConsentDetailModal(null)} className="w-full py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">
            닫기
          </button>
        </div>
      </div>
    )}
    </>
  );
};
